import type {
  AccessCatalog,
  ClientSwitch,
  ClientSwitchField,
} from "types/interfaces/access";
import type { GrantedLine } from "./permissionsPanelModel";

/** The switches of a client user, and the value that picks a switch's choice. */
export type ClientSwitchState = Partial<Record<ClientSwitchField, boolean>> & {
  integrations_experience?: string;
};

function isOn(state: ClientSwitchState, item: ClientSwitch): boolean {
  return (
    state[item.field] === true &&
    (item.parent == null || state[item.parent] === true)
  );
}

function grantsOf(state: ClientSwitchState, item: ClientSwitch): string {
  if (!item.choice_field) return item.grants;
  const value = state[item.choice_field];
  // A profile without the value has the first choice, as the server defaults it.
  const choice = item.choices.find((c) => c.value === value) ?? item.choices[0];
  return choice?.grants ?? item.grants;
}

/** What a client user's switches open, worded by the catalog; the pages every
 * client user opens come last. */
export function clientGrantedLines(
  catalog: AccessCatalog,
  state: ClientSwitchState,
): GrantedLine[] {
  const lines = catalog.client_user.switches
    .filter((item) => isOn(state, item))
    .map((item) => ({
      key: item.field,
      name: item.name,
      grants: grantsOf(state, item),
    }));
  return [...lines, ...catalog.client_user.always];
}

/** The switches that are off. A sub-option is named only while its parent is on:
 * without the parent it says nothing new. */
export function clientNoAccessNames(
  catalog: AccessCatalog,
  state: ClientSwitchState,
): string[] {
  return catalog.client_user.switches
    .filter(
      (item) =>
        !isOn(state, item) &&
        (item.parent == null || state[item.parent] === true),
    )
    .map((item) => item.name);
}

/** The top-level switches, each with the sub-options that hang off it. */
export function switchTree(
  catalog: AccessCatalog,
): { item: ClientSwitch; children: ClientSwitch[] }[] {
  const all = catalog.client_user.switches;
  return all
    .filter((item) => item.parent == null)
    .map((item) => ({
      item,
      children: all.filter((child) => child.parent === item.field),
    }));
}
